import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { SchoolLevel } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

// `live_participations.test_anchor_at` (migration 0177) postérieur aux types
// stale → client générique, comme les autres hooks live.
const db = supabase as unknown as SupabaseClient;

/** Défi Live School dont l'exercice courant est la source (`source_exercise_id`). */
export interface LiveChallengeForExercise {
  id: string;
  level: SchoolLevel;
  starts_at: string;
}

/**
 * Choisit LE défi à tester pour un exercice source diffusé plusieurs fois : le
 * plus PROCHE à venir (le plus sûr à tester — supprimable avant le vrai créneau),
 * sinon le plus récent passé. Pur & testable.
 */
export function pickTestChallenge<T extends { starts_at: string }>(
  rows: readonly T[],
  nowMs: number,
): T | null {
  if (rows.length === 0) return null;
  const upcoming = rows
    .filter((r) => Date.parse(r.starts_at) > nowMs)
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));
  if (upcoming[0]) return upcoming[0];
  return [...rows].sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at))[0] ?? null;
}

/**
 * Défi Live School dont `exerciseId` est la source, à tester en conditions réelles
 * (chrono) via `/live/<niveau>?test=<challengeId>`. ⚠️ RLS `live_challenges` : un
 * défi `scheduled` (à venir) n'est lisible QUE par un admin — réservé aux
 * testeurs/admins côté UI (cf. `useLiveSchoolExercises`).
 */
export function useLiveChallengeForExercise(exerciseId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['live-challenge-for-exercise', exerciseId],
    enabled: enabled && !!exerciseId,
    staleTime: 30_000,
    queryFn: async (): Promise<LiveChallengeForExercise | null> => {
      const { data, error } = await db
        .from('live_challenges')
        .select('id, level, starts_at')
        .eq('source_exercise_id', exerciseId);
      if (error) throw error;
      return pickTestChallenge((data ?? []) as LiveChallengeForExercise[], Date.now());
    },
  });
}

function key(userId: string) {
  return ['live-test-participations', userId] as const;
}

/** Ensemble des `challenge_id` sur lesquels le testeur a une participation de TEST. */
export function useMyTestChallengeIds(userId: string) {
  return useQuery({
    queryKey: key(userId),
    enabled: !!userId,
    staleTime: 15_000,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await db
        .from('live_participations')
        .select('challenge_id')
        .eq('student_id', userId)
        .not('test_anchor_at', 'is', null);
      if (error) throw error;
      return new Set(((data ?? []) as Array<{ challenge_id: string }>).map((r) => r.challenge_id));
    },
  });
}

/** Supprime la participation de test d'un défi (edge fn `live-test` → refresh classement). */
export function useDeleteTestParticipation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (challengeId: string) => {
      const { error } = await supabase.functions.invoke('live-test', {
        body: { action: 'delete', challenge_id: challengeId },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(userId) });
    },
  });
}
